/**
 * Interior (mockup) PSD upload — presigned S3 flow.
 *
 * The legacy `uploadinterior.php` endpoint received the whole PSD through the web
 * server (blocked by the ~20MB upload limit) and split it into per-layer PNGs with
 * ImageMagick. This module does the layer splitting in the browser instead:
 *
 *   1. Parse the PSD with ag-psd.
 *   2. Render each mapped layer onto a full-canvas transparent PNG (matching the old
 *      `convert -dispose Background -layers coalesce` output, which padded each layer
 *      to canvas size without accumulation).
 *   3. PUT each PNG straight to S3 via presigned URLs from `interiors.uploadPresign`.
 *   4. Record the `pictufy_interiors` row via `interiors.uploadComplete`.
 *
 * Admin-only; wired to the "Upload 2D" (#uploadinterior, mode 1) and "Upload 3D"
 * (#uploadinterior3d, mode 2) buttons on /manageinteriors.
 */

// ag-psd (plus its `pako` dependency) is ~210 kB and is only reachable from the
// admin mockup upload below, so it is imported on demand — types only up here.
import type { Layer, Psd } from "ag-psd";
import { api } from "../api";
import { showLoadingOverlay, hideLoadingOverlay, toastError } from "../ui/messages";
import { getDomElement } from "../utils/dom";
import Dropzone from "dropzone";

// Component column order per mode. Index i maps to PSD layer i (bottom→top),
// i.e. ag-psd `psd.children[i]`. Mirrors the layer map in uploadinterior.php.
const COLUMNS_2D = ["background", "canvas", "shadow", "highlight", "overlay"] as const;
const COLUMNS_3D = ["background", "canvas", "canvas2", "shadow", "highlight", "overlay"] as const;

/**
 * Render a single PSD layer onto a full-canvas transparent PNG blob.
 * Returns null when the layer has no pixel data (treated as a missing component).
 */
async function layerToPngBlob(psd: Psd, layer: Layer | undefined): Promise<Blob | null> {
  const src = layer?.canvas;
  if (!src || src.width === 0 || src.height === 0) return null;

  const out = document.createElement("canvas");
  out.width = psd.width;
  out.height = psd.height;
  const ctx = out.getContext("2d");
  if (!ctx) return null;
  ctx.drawImage(src, layer?.left ?? 0, layer?.top ?? 0);

  return await new Promise<Blob | null>((resolve) => {
    out.toBlob((blob) => {
      resolve(blob);
    }, "image/png");
  });
}

/**
 * Full pipeline for one dropped PSD file.
 */
async function handleInteriorPsd(file: File, mode: 1 | 2): Promise<void> {
  if (!/\.psd$/i.test(file.name)) {
    toastError("Please upload a .psd file");
    return;
  }

  showLoadingOverlay("Reading PSD...");

  let psd: Psd;
  try {
    const [{ readPsd }, buffer] = await Promise.all([import("ag-psd"), file.arrayBuffer()]);
    psd = readPsd(buffer, { skipCompositeImageData: true, skipThumbnail: true });
  } catch {
    hideLoadingOverlay();
    toastError("Failed to read PSD file");
    return;
  }

  if (
    !psd.width ||
    !psd.height ||
    psd.width < 1 ||
    psd.width > 10000 ||
    psd.height < 1 ||
    psd.height > 10000
  ) {
    hideLoadingOverlay();
    toastError("Canvas size out of range: " + psd.width + "x" + psd.height);
    return;
  }

  const children = psd.children ?? [];
  if (children.length < 2) {
    hideLoadingOverlay();
    toastError("PSD does not contain the required layers (need at least background + canvas)");
    return;
  }

  // Build the PNG blob for each mapped column (null when the layer is absent).
  const columns = mode === 2 ? COLUMNS_3D : COLUMNS_2D;
  showLoadingOverlay("Splitting layers...");

  const present: { column: string; blob: Blob }[] = [];
  for (let i = 0; i < columns.length; i++) {
    const blob = await layerToPngBlob(psd, children[i]);
    const column = columns[i];
    if (blob && column !== undefined) present.push({ column, blob });
  }

  if (
    !present.some((p) => p.column === "background") ||
    !present.some((p) => p.column === "canvas")
  ) {
    hideLoadingOverlay();
    toastError("PSD is missing the required background or canvas layer");
    return;
  }

  // Request one presigned PUT URL per present layer.
  showLoadingOverlay("Preparing upload...");
  let slots: { uuid: string; url: string }[];
  try {
    const data = await api.mockups.uploadPresign({ count: present.length });
    if (!data.slots || data.slots.length < present.length) {
      throw new Error("presign");
    }
    slots = data.slots;
  } catch {
    hideLoadingOverlay();
    toastError("Could not prepare upload");
    return;
  }

  // PUT each layer PNG straight to S3.
  const uuidByColumn: Record<string, string> = {};
  for (let i = 0; i < present.length; i++) {
    showLoadingOverlay("Uploading layer " + (i + 1) + " of " + present.length + "...");
    const slot = slots[i];
    const layer = present[i];
    if (!slot || !layer) continue;
    try {
      const putRes = await fetch(slot.url, {
        method: "PUT",
        headers: { "Content-Type": "image/png" },
        body: layer.blob,
      });
      if (!putRes.ok) throw new Error(String(putRes.status));
    } catch {
      hideLoadingOverlay();
      toastError("Failed to upload layer " + (i + 1));
      return;
    }
    uuidByColumn[layer.column] = slot.uuid;
  }

  // Persist the interior record.
  showLoadingOverlay("Saving...");
  let interiorId: string;
  try {
    const result = await api.mockups.uploadComplete({
      mode,
      filename: file.name,
      canvas_width: psd.width,
      canvas_height: psd.height,
      background: uuidByColumn.background,
      canvas: uuidByColumn.canvas,
      canvas2: uuidByColumn.canvas2,
      shadow: uuidByColumn.shadow,
      highlight: uuidByColumn.highlight,
      overlay: uuidByColumn.overlay,
    });
    interiorId = result.interiorId;
  } catch (err) {
    hideLoadingOverlay();
    toastError((err as Error).message || "Could not save interior");
    return;
  }

  hideLoadingOverlay();

  // Show the result. This used to open the editor on top of the page as it was, so when the
  // editor closed the list underneath was still the one rendered before the upload: the new
  // interior was in the database and its layers were in S3, and nothing on screen said so.
  // The reporter's PSD was uploaded three times over two weeks — each one succeeded.
  //
  // Now the page reloads deep-linked to the new tile (`/manageinteriors/<tab>/<id>`, which
  // `data-init-scroll-to` scrolls to — the legacy flow) with the editor opened on top of it,
  // so the tile is there when the editor closes. A new upload is ART_PRINT with no reseller,
  // which is the "for-all" tab whatever tab the upload button was pressed on.
  location.href = `/manageinteriors/for-all/${interiorId}?edit=1`;
}

/**
 * Attach a Dropzone (for the drag/drop + click affordance) to an interior upload
 * button. Dropzone's own uploader is disabled (`autoProcessQueue: false`); the whole
 * presign → PUT → complete pipeline runs in `addedfile`.
 */
function attachInteriorDropzone(selector: string, mode: 1 | 2): void {
  const el = getDomElement(selector.replace(/^#/, ""));
  if (!el) return;

  new Dropzone(el, {
    url: "#",
    autoProcessQueue: false,
    maxFilesize: 5000,
    acceptedFiles: ".psd,image/vnd.adobe.photoshop,image/psd,application/x-photoshop",
    createImageThumbnails: false,
    previewTemplate: "",
    addedfile: (file: DropzoneFile) => {
      void handleInteriorPsd(file, mode);
    },
    accept: (_file: DropzoneFile, done: (error?: string) => void) => {
      done();
    },
  });
}

export function initInteriorUpload(): void {
  attachInteriorDropzone("#uploadinterior", 1);
  attachInteriorDropzone("#uploadinterior3d", 2);
}
